import React, { useState, useEffect, useRef ,useMemo,useCallback} from "react";
import { Prism as SyntaxHighlighter } from "react-syntax-highlighter";
import { oneDark } from "react-syntax-highlighter/dist/esm/styles/prism";
import { API_BASE, EXEC_APPENDER } from "../utils/constants";
// import removed to avoid duplicate
import { fetchTextWithDiagnostics, safeArr, stripCodeWrappers, looksLikeCode, guessLang } from "../utils/helpers";
import { normalizeQuestion } from "../utils/mappers";
import { PrimaryButton, SecondaryButton, DangerLink, Chip, Skeleton } from "../components/UI";

// Helper function to get or create exec_id
async function getOrCreateExecId({ token, appName, section, category, onUnauthorized }) {
  const base = API_BASE.replace(/\/$/, "");

  const searchUrl =
    `${base}/exams${EXEC_APPENDER}/${encodeURIComponent(appName)}` +
    `/users/search_exam_exec${EXEC_APPENDER}` +
    `?category_name=${encodeURIComponent(category)}` +
    `&section_name=${encodeURIComponent(section)}` +
    `&is_completed=N`;

  const searchRes = await fetchTextWithDiagnostics(searchUrl, {
    headers: { Authorization: token },
  });

  if (searchRes.status === 401) return onUnauthorized?.(), null;

  if (searchRes.ok) {
    try {
      const arr = JSON.parse(searchRes.text);
      const list = Array.isArray(arr) ? arr : [];
      if (list.length > 0) {
        list.sort(
          (a, b) => Number(b?.exec_id ?? b?.execId ?? 0) - Number(a?.exec_id ?? a?.execId ?? 0)
        );
        const existing = list[0]?.exec_id ?? list[0]?.execId ?? null;
        if (existing != null) return Number(existing);
      }
    } catch {}
  }

  const createUrl =
    `${base}/exams${EXEC_APPENDER}/${encodeURIComponent(appName)}` +
    `/users/exam_exec${EXEC_APPENDER}`;

  const payload = {
    app_name: appName,
    category_name: category,
    section_name: section,
    is_completed: "N",
  };

  const createRes = await fetchTextWithDiagnostics(createUrl, {
    method: "POST",
    headers: { Authorization: token, "Content-Type": "application/json" },
    body: JSON.stringify(payload),
  });

  if (createRes.status === 401) return onUnauthorized?.(), null;
  if (!createRes.ok) return null;

  try {
    const obj = JSON.parse(createRes.text);
    const createdId = obj?.exec_id ?? obj?.execId ?? obj?.id ?? null;
    return createdId != null ? Number(createdId) : null;
  } catch {
    return null;
  }
}

export function QuestionWizard({
  token,
  appName,
  section,
  category,
  categoryList = [],
  categoryIndex = 0,
  filter,
  mode,
  onNavigateCategory,
  onBack,
  onLogout,
  onUnauthorized,
  isDarkMode,
}) {
  const [questions, setQuestions] = useState([]);
  const [index, setIndex] = useState(0);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState("");

  const [answers, setAnswers] = useState({});
  const [revealed, setRevealed] = useState({});

  const [execId, setExecId] = useState(null);
  const execIdRef = useRef(null);
  const execIdByCategoryRef = useRef({});

  const [saving, setSaving] = useState(false);
  const [saveError, setSaveError] = useState("");

  useEffect(() => {
    execIdRef.current = execId;
  }, [execId]);

  useEffect(() => {
    async function load() {
      setLoading(true);
      setError("");
      setSaveError("");
      setSaving(false);
      setExecId(null);
      execIdRef.current = null;
      execIdByCategoryRef.current = {};

      setQuestions([]);
      setIndex(0);
      setAnswers({});
      setRevealed({});

      const base = API_BASE.replace(/\/$/, "");
      let normalized = [];
      
      // For filtered view (wrong/unanswered), load attempts per category
      if (filter === "wrong" || filter === "unanswered") {
        const categoriesToLoad = category ? [category] : categoryList;
        for (const cat of categoriesToLoad) {
          const wrongIds = new Set();
          const attemptedIds = new Set();
          
          // Fetch both completed and incomplete attempts
          for (const isCompleted of ["Y", "N"]) {
            const attUrl =
              `${base}/exams${EXEC_APPENDER}/${encodeURIComponent(appName)}` +
              `/users/search_exam_exec${EXEC_APPENDER}` +
              `?category_name=${encodeURIComponent(cat)}` +
              `&section_name=${encodeURIComponent(section)}` +
              `&is_completed=${isCompleted}`;

            const attRes = await fetchTextWithDiagnostics(attUrl, {
              headers: { Authorization: token },
            });

            if (attRes.ok) {
              try {
                const attempts = JSON.parse(attRes.text);
                for (const att of safeArr(attempts)) {
                  const childs = safeArr(att?.lstChilds || att?.lstChild || att?.children || att?.childs);
                  for (const child of childs) {
                    const examId = String(child?.exam_id ?? child?.examId ?? "").trim();
                    if (examId) {
                      attemptedIds.add(examId);
                      const rw = String(child?.right_or_wrong || "").toLowerCase().trim();
                      if (rw === "wrong" || rw === "0" || rw === "no") {
                        wrongIds.add(examId);
                      }
                    }
                  }
                }
              } catch {
                // Continue without attempts data
              }
            }
          }

          const url = `${base}/exams${EXEC_APPENDER}/${encodeURIComponent(appName)}/${encodeURIComponent(section)}/${encodeURIComponent(cat)}?CurrentExamID=0&prevNext=Next`.replace(/\+/g, "%2B");
          const res = await fetchTextWithDiagnostics(url, {
            headers: { Authorization: token },
          });

          if (res.ok) {
            try {
              const arr = JSON.parse(res.text);
              const catQuestions = (Array.isArray(arr) ? arr : []).map((q) => ({
                ...normalizeQuestion(q),
                category: cat,
              }));

              if (filter === "wrong") {
                normalized = normalized.concat(
                  catQuestions.filter((q) => {
                    const qId = String(q.exam_id || q.examId || q.id || "").trim();
                    return wrongIds.has(qId);
                  })
                );
              } else if (filter === "unanswered") {
                normalized = normalized.concat(
                  catQuestions.filter((q) => {
                    const qId = String(q.exam_id || q.examId || q.id || "").trim();
                    return !attemptedIds.has(qId);
                  })
                );
              }
            } catch {
              // Continue
            }
          }
        }
      } else {
        // Normal mode: load all questions
        const categoriesToLoad = category ? [category] : categoryList;
        
        for (const cat of categoriesToLoad) {
          const url = `${base}/exams${EXEC_APPENDER}/${encodeURIComponent(appName)}/${encodeURIComponent(section)}/${encodeURIComponent(cat)}?CurrentExamID=0&prevNext=Next`.replace(/\+/g, "%2B");

          const res = await fetchTextWithDiagnostics(url, {
            headers: { Authorization: token },
          });

          if (res.status === 401) return onUnauthorized?.();
          if (!res.ok) continue;

          try {
            const arr = JSON.parse(res.text);
            normalized = normalized.concat((Array.isArray(arr) ? arr : []).map(normalizeQuestion));
          } catch {
            // Continue
          }
        }
      }

      if (normalized.length === 0) {
        setError(filter ? `No ${filter} questions found.` : "No questions found.");
        setLoading(false);
        return;
      }

      setQuestions(normalized);
      setLoading(false);
    }
    load();
  }, [token, appName, section, category, categoryList, filter, onUnauthorized]);

  const q = questions[index] || {};
  const qid = q.exam_id ?? q.examId ?? q.id ?? String(index);

  const letters = ["A", "B", "C", "D", "E", "F"];

  const choices = useMemo(() => {
    const byNumber = letters
      .map((L, i) => {
        const idx = i + 1;
        const val = q[`answer${idx}`] || q[`Answer${idx}`];
        const img = q[`answerImage${idx}`] || q[`AnswerImage${idx}`];
        return val ? { key: L, val, img } : null;
      })
      .filter(Boolean);

    const byLetter = letters
      .map((L) => {
        const val = q[`option${L}`] || q[`Option${L}`];
        const img = q[`optionImage${L}`] || q[`OptionImage${L}`];
        return val ? { key: L, val, img } : null;
      })
      .filter(Boolean);

    return byNumber.length ? byNumber : byLetter;
  }, [q]);

  const selected = answers[String(qid)];
  const isRevealed = !!revealed[String(qid)];
  const isExamMode = mode === "exam";

  const explanation =
    q.explanation || q.explaination || q.answerExplanation || q.solution || "";

  let correct = q.correct || q.correctAnswer || q.answerKey || null;

  if (!correct) {
    for (let i = 1; i <= 6; i++) {
      const val = q[`isAnswerCorrect${i}`] || q[`IsAnswerCorrect${i}`];
      if (
        val &&
        (val === true || val === "true" || val === "1" || val === "Y")
      ) {
        correct = letters[i - 1];
        break;
      }
    }
  }

  const saveChild = useCallback(
    async ({ qObj, selectedLetter, correctLetter }) => {
      setSaveError("");
      if (!selectedLetter) return;

      const saveCategory =
        category || qObj?.category || qObj?.Category || qObj?.category_name;

      const isFiltered = filter === "wrong" || filter === "unanswered";
      let id = isFiltered && saveCategory
        ? execIdByCategoryRef.current[saveCategory]
        : execIdRef.current;

      if (!id) {
        id = await getOrCreateExecId({
          token,
          appName,
          section,
          category: saveCategory,
          onUnauthorized,
        });
        if (!id) {
          setSaveError("Could not find/create attempt (exec_id).\n");
          return;
        }
        if (isFiltered && saveCategory) {
          execIdByCategoryRef.current[saveCategory] = id;
        } else {
          execIdRef.current = id;
        }
        setExecId(id);
      }

      const base = API_BASE.replace(/\/$/, "");
      const examIdRaw =
        qObj?.exam_id ?? qObj?.examId ?? qObj?.id ?? qObj?.qid;
      const examId = Number(examIdRaw);
      if (!Number.isFinite(examId)) {
        setSaveError("Cannot save: exam_id missing in question payload");
        return;
      }

      const chosen = {
        answer_choosen1: selectedLetter === "A" ? "Y" : "N",
        answer_choosen2: selectedLetter === "B" ? "Y" : "N",
        answer_choosen3: selectedLetter === "C" ? "Y" : "N",
        answer_choosen4: selectedLetter === "D" ? "Y" : "N",
      };

      const correctFlags = {
        isAnswerCorrect1: correctLetter === "A" ? "Y" : "N",
        isAnswerCorrect2: correctLetter === "B" ? "Y" : "N",
        isAnswerCorrect3: correctLetter === "C" ? "Y" : "N",
        isAnswerCorrect4: correctLetter === "D" ? "Y" : "N",
      };

      const right_or_wrong =
        correctLetter && selectedLetter === correctLetter
          ? "Right"
          : "Wrong";

      const payload = {
        app_name: appName,
        exec_id: Number(id),
        exam_id: Number(examId),
        exam_name:
          qObj?.exam_name || qObj?.question || qObj?.title || "",
        ...chosen,
        ...correctFlags,
        right_or_wrong,
      };

      const url =
        `${base}/exams${EXEC_APPENDER}/${encodeURIComponent(appName)}` +
        `/users/exam_exec${EXEC_APPENDER}/${encodeURIComponent(id)}` +
        `/exam_exec_child${EXEC_APPENDER}`;

      setSaving(true);
      const res = await fetchTextWithDiagnostics(url, {
        method: "POST",
        headers: {
          Authorization: token,
          "Content-Type": "application/json",
        },
        body: JSON.stringify(payload),
      });
      setSaving(false);

      if (res.status === 401) return onUnauthorized?.();
      if (!res.ok) {
        console.error("Save child failed:", res.status, res.text);
        setSaveError(`Save failed (status ${res.status})`);
      }
    },
    [token, appName, section, category, filter, onUnauthorized]
  );

  const selectChoice = (k) =>
    setAnswers((prev) => ({ ...prev, [String(qid)]: k }));
  const reveal = () =>
    setRevealed((prev) => ({ ...prev, [String(qid)]: true }));
  const hideReveal = () =>
    setRevealed((prev) => ({ ...prev, [String(qid)]: false }));

  const prev = () => setIndex((i) => Math.max(0, i - 1));
  const next = () =>
    setIndex((i) => Math.min(questions.length - 1, i + 1));

  const isFiltered = filter === "wrong" || filter === "unanswered";
  const hasNextCategory =
    !isFiltered && categoryIndex < (categoryList?.length || 0) - 1;
  const nextCategoryName = hasNextCategory
    ? categoryList[categoryIndex + 1]
    : "";
  const atLastQuestion = index === questions.length - 1;

  return (
    <div
      className={`rounded-3xl border ${isDarkMode ? 'border-slate-700 bg-slate-800' : 'border-slate-300 bg-white'} shadow-sm`}
      data-tour="question-card"
    >
      <div className={`px-6 py-5 border-b ${isDarkMode ? 'border-slate-700' : 'border-slate-200'}`}>
        <div className="flex items-center justify-between gap-3 flex-wrap">
          <button
            onClick={onBack}
            className={`text-sm font-semibold ${isDarkMode ? 'text-indigo-200' : 'text-indigo-600'} hover:underline`}
          >
            ← Back
          </button>

          <div className="flex items-center gap-3">
            <Chip>
              {questions.length
                ? `Question ${index + 1} / ${questions.length}`
                : "Questions"}
            </Chip>
            <DangerLink
              href="#"
              onClick={(e) => {
                e.preventDefault();
                onLogout();
              }}
            >
              Logout
            </DangerLink>
          </div>
        </div>

        {execId ? (
          <div className={`mt-3 text-xs ${isDarkMode ? 'text-slate-400' : 'text-slate-500'}`}>
            Attempt exec_id:{" "}
            <span className={`font-semibold ${isDarkMode ? 'text-slate-300' : 'text-slate-700'}`}>{execId}</span>
            {saving ? <span className="ml-2">• Saving…</span> : null}
            {saveError ? (
              <span className="ml-2 text-red-600">• {saveError}</span>
            ) : null}
          </div>
        ) : saveError ? (
          <div className="mt-3 text-xs text-red-600">{saveError}</div>
        ) : null}
      </div>

      <div className="px-6 py-6">
        {loading ? (
          <div className="space-y-3">
            <Skeleton className="h-7 w-2/3" />
            <Skeleton className="h-16 w-full" />
            <Skeleton className="h-12 w-full" />
            <Skeleton className="h-12 w-full" />
          </div>
        ) : error ? (
          <div className={`rounded-2xl border ${isDarkMode ? 'border-red-800 bg-red-900/30' : 'border-red-200 bg-red-50'} p-4 ${isDarkMode ? 'text-red-400' : 'text-red-700'}`}>
            {error}
          </div>
        ) : questions.length === 0 ? (
          <div className={`rounded-2xl border ${isDarkMode ? 'border-slate-700 bg-slate-800' : 'border-slate-200 bg-slate-50'} p-6 ${isDarkMode ? 'text-slate-300' : 'text-slate-700'}`}>
            No questions found.
          </div>
        ) : (
          <>
            <div className="mb-4">
              <div className={`text-xl font-semibold ${isDarkMode ? 'text-white' : 'text-slate-900'} whitespace-pre-wrap`}>
                {q.title ||
                  q.question ||
                  q.heading ||
                  `${section} / ${category}`}
              </div>
              {q.description ? (
                <div className={`mt-2 ${isDarkMode ? 'text-slate-300' : 'text-slate-700'} whitespace-pre-wrap`}>
                  {q.description}
                </div>
              ) : null}
            </div>

            {choices.length > 0 ? (
              <div className="space-y-2" data-tour="question-answers">
                {choices.map((c) => {
                  const normalizedCorrect = String(correct || "").toUpperCase();
                  const isCorrect = normalizedCorrect && c.key === normalizedCorrect;
                  const isSelected = selected === c.key;
                  
                  // In exam mode, don't show correct/incorrect colors until revealed
                  const shouldShowCorrectness = !isExamMode || isRevealed;

                  const box =
                    isRevealed && isCorrect
                      ? isDarkMode
                        ? "border-emerald-500 bg-emerald-900/40"
                        : "border-emerald-400 bg-emerald-100"
                      : shouldShowCorrectness && isSelected && normalizedCorrect && !isCorrect
                      ? isDarkMode
                        ? "border-red-500 bg-red-900/60"
                        : "border-red-500 bg-red-200"
                      : isSelected
                      ? isDarkMode
                        ? "border-indigo-500 bg-indigo-900/30"
                        : "border-indigo-400 bg-indigo-100"
                      : isDarkMode
                      ? "border-slate-600 bg-slate-700 hover:bg-slate-600"
                      : "border-slate-300 bg-slate-50 hover:bg-slate-100";

                  const optionTextClass =
                    isRevealed && isCorrect
                      ? "font-semibold"
                      : shouldShowCorrectness && isSelected && normalizedCorrect && !isCorrect
                      ? "font-semibold"
                      : isSelected
                      ? isDarkMode
                        ? "text-indigo-100 font-semibold"
                        : "text-slate-900 font-semibold"
                      : isDarkMode
                      ? "text-slate-200"
                      : "text-slate-900";

                  const optionTextStyle =
                    isRevealed && isCorrect
                      ? isDarkMode
                        ? { color: "#ffffff", fontWeight: 700, opacity: 1 }
                        : { color: "#065f46", fontWeight: 700, opacity: 1 }
                      : shouldShowCorrectness && isSelected && normalizedCorrect && !isCorrect
                      ? isDarkMode
                        ? { color: "#ffffff", fontWeight: 800, opacity: 1, textShadow: "0 1px 2px rgba(0, 0, 0, 0.4)" }
                        : { color: "#7f1d1d", fontWeight: 800, opacity: 1 }
                      : isSelected && isDarkMode
                      ? { color: "#ffffff", fontWeight: 600, opacity: 1 }
                      : isDarkMode
                      ? { color: "#e0e7ff", fontWeight: 500, opacity: 1 }
                      : { color: "#000000", fontWeight: 500, opacity: 1 };

                  return (
                    <label
                      key={c.key}
                      className={`flex items-start gap-3 p-3 rounded-2xl border transition opacity-100 ${box}`}
                    >
                      <input
                        type="radio"
                        name={`q_${qid}`}
                        checked={isSelected}
                        onChange={() => selectChoice(c.key)}
                        className="mt-1"
                      />
                      <div className="flex-1">
                        <div
                          className={`text-sm ${optionTextClass} whitespace-pre-wrap`}
                          style={optionTextStyle}
                        >
                          <span className="font-semibold mr-2">
                            {c.key}.
                          </span>
                          {c.val}
                        </div>
                        {c.img ? (
                          <img
                            src={c.img}
                            alt={`Option ${c.key}`}
                            className="mt-2 max-h-44 object-contain rounded-xl border border-slate-200"
                          />
                        ) : null}
                      </div>
                    </label>
                  );
                })}

                {!isExamMode && correct && selected ? (
                  <div
                    className={`mt-3 text-sm font-semibold ${
                      selected === String(correct || "").toUpperCase()
                        ? "text-emerald-700"
                        : "text-red-700"
                    }`}
                  >
                    {selected === String(correct || "").toUpperCase()
                      ? "Correct ✅"
                      : `Incorrect ❌ (Answer: ${String(correct || "").toUpperCase()})`}
                  </div>
                ) : null}

                {!isExamMode ? (
                  <div className="flex items-center gap-2 pt-3 flex-wrap">
                    <PrimaryButton
                      onClick={reveal}
                      disabled={!correct || isRevealed}
                      title="Reveal the correct answer for this question"
                    >
                      Show Correct Answer
                    </PrimaryButton>
                    {isRevealed ? (
                      <SecondaryButton onClick={hideReveal} title="Hide the correct answer">
                        Hide
                      </SecondaryButton>
                    ) : null}
                  </div>
                ) : null}

                {!isExamMode &&
                explanation &&
                (isRevealed ||
                  (selected && selected !== correct)) ? (
                  <div className={`mt-4 rounded-2xl border ${isDarkMode ? 'border-slate-700 bg-slate-800' : 'border-slate-300 bg-white'} p-4`}>
                    <div className={`text-[11px] uppercase tracking-wide ${isDarkMode ? 'text-slate-400' : 'text-slate-600'} font-semibold mb-2`}>
                      Explanation
                    </div>

                    {looksLikeCode(
                      stripCodeWrappers(explanation)
                    ) ? (
                      <SyntaxHighlighter
                        language={
                          guessLang(
                            stripCodeWrappers(explanation)
                          ) || "text"
                        }
                        style={oneDark}
                        wrapLongLines
                        customStyle={{
                          borderRadius: "1rem",
                          fontSize: "0.85rem",
                        }}
                      >
                        {stripCodeWrappers(explanation)}
                      </SyntaxHighlighter>
                    ) : (
                      <div
                        className={`text-sm leading-relaxed ${isDarkMode ? 'text-slate-300' : 'text-slate-700'}`}
                        style={{
                          wordWrap: 'break-word'
                        }}
                        dangerouslySetInnerHTML={{
                          __html: (() => {
                            // Decode HTML entities
                            let decoded = String(explanation)
                              .replace(/&lt;/g, '<')
                              .replace(/&gt;/g, '>')
                              .replace(/&amp;/g, '&')
                              .replace(/&quot;/g, '"')
                              .replace(/&#39;/g, "'");
                            
                            // Apply styling to HTML tags
                            return decoded
                              .replace(/<strong>/gi, `<strong style="font-weight: 700; color: ${isDarkMode ? '#fff' : '#1f2937'}">`)
                              .replace(/<p>/gi, `<p style="margin: 0.5em 0; color: ${isDarkMode ? '#d1d5db' : '#374151'}">`)
                              .replace(/<br\s*\/?>/gi, '<br/>')
                              .replace(/<li>/gi, '<li style="margin-left: 1.25rem; list-style-type: disc">')
                              .replace(/<ul>/gi, '<ul style="margin: 0.5rem 0; padding-left: 1.5rem">');
                          })()
                        }}
                      />
                    )}
                  </div>
                ) : null}
              </div>
            ) : (
              <div className={`rounded-2xl border ${isDarkMode ? 'border-slate-700 bg-slate-800' : 'border-slate-200 bg-slate-50'} p-6 ${isDarkMode ? 'text-slate-300' : 'text-slate-700'}`}>
                No choices found for this question.
              </div>
            )}

            <div className="flex items-center gap-2 pt-6 flex-wrap">
              <SecondaryButton
                onClick={() => {
                  setRevealed((r) => ({
                    ...r,
                    [String(qid)]: false,
                  }));
                  prev();
                }}
                disabled={index === 0 || saving}
              >
                Prev
              </SecondaryButton>

              <PrimaryButton
                onClick={async () => {
                  setRevealed((r) => ({
                    ...r,
                    [String(qid)]: false,
                  }));
                  await saveChild({
                    qObj: q,
                    selectedLetter: selected,
                    correctLetter: correct || "",
                  });
                  next();
                }}
                disabled={index === questions.length - 1 || saving}
              >
                {saving ? "Saving…" : "Next"}
              </PrimaryButton>

              {atLastQuestion && hasNextCategory ? (
                <PrimaryButton
                  className="bg-emerald-600 hover:bg-emerald-700"
                  onClick={async () => {
                    await saveChild({
                      qObj: q,
                      selectedLetter: selected,
                      correctLetter: correct || "",
                    });
                    onNavigateCategory?.(categoryIndex + 1);
                  }}
                  disabled={saving}
                >
                  Next Category: {nextCategoryName} →
                </PrimaryButton>
              ) : null}

              {atLastQuestion && !hasNextCategory ? (
                <PrimaryButton
                  className="bg-emerald-600 hover:bg-emerald-700"
                  onClick={async () => {
                    await saveChild({
                      qObj: q,
                      selectedLetter: selected,
                      correctLetter: correct || "",
                    });
                    onBack?.();
                  }}
                  disabled={saving}
                >
                  {saving ? "Saving…" : "Save & Finish"}
                </PrimaryButton>
              ) : null}
            </div>
          </>
        )}
      </div>
    </div>
  );
}

// =====================
// ResultsViewer
// =====================
